import React, { useState, useEffect, useRef } from 'react';
import BasketSidebar, { TreeItem } from '../components/BasketSidebar';
import ProfileSidebar from '../components/ProfileSidebar';
import TrackOrdersSidebar, { OrderItem } from '../components/TrackOrdersSidebar';
import Footer from '../components/Footer';
import JoinUpdates from '../components/JoinUpdates';
import Navbar from '../components/Navbar';
import AddressModal from '../components/AddressModal';
import DeleteAccountConfirmModal from '../components/DeleteAccountConfirmModal';
import DeleteAccountSuccessModal from '../components/DeleteAccountSuccessModal';
import PaymentSuccessModal from '../components/PaymentSuccessModal';
import { fetchWithAuth } from '../lib/fetchWithAuth';
import { useBasket } from '../hooks/useBasket';
import { useUserProfile } from '../hooks/useUserProfile';
import { useAccountDeletion } from '../hooks/useAccountDeletion';
import { useAddressAutocomplete } from '../hooks/useAddressAutocomplete';

interface AddTreePageProps {
    session: any;
    logout: () => void;
}

const AddTreePage: React.FC<AddTreePageProps> = ({ session, logout }) => {
    const [trees, setTrees] = useState<TreeItem[]>([]);
    const [treeName, setTreeName] = useState('');
    const [treePrice, setTreePrice] = useState('');

    const handleLogout = logout;

    // --- BASKET ---
    const {
        basket, isBasketOpen, setIsBasketOpen, flashBasket, checkoutLoading,
        clientSecret, setClientSecret, showPaymentSuccess, setShowPaymentSuccess,
        updateQuantity, removeFromBasket, handleCheckout, handlePaymentSuccess,
        totalItemsCount, grandTotalPrice,
    } = useBasket();

    // --- TRACK ORDERS ---
    const [isTrackOrdersOpen, setIsTrackOrdersOpen] = useState(false);
    const [userOrders, setUserOrders] = useState<OrderItem[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);

    // --- FORM STATES ---
    const [treeImageFile, setTreeImageFile] = useState<File | null>(null);
    const [treeImagePreview, setTreeImagePreview] = useState<string>('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [customAlert, setCustomAlert] = useState<{ show: boolean; title: string; message: string } | null>(null);
    const [locationAlert, setLocationAlert] = useState<'required' | 'error' | null>(null);

    // --- PROFILE ---
    const {
        isProfileOpen, setIsProfileOpen, isEditingProfile, setIsEditingProfile,
        profileName, setProfileName, profileAddress, profileCity, profileState, profileZipCode,
        saveLoading, fetchUserProfile, updateProfile,
    } = useUserProfile(handleLogout);
    const [userHomeLocation, setUserHomeLocation] = useState<{ latitude: number | null; longitude: number | null }>({ latitude: null, longitude: null });

    // --- ACCOUNT DELETION ---
    const {
        showDeleteConfirm, setShowDeleteConfirm, showDeleteSuccess, deleteLoading, executeAccountDeletion,
    } = useAccountDeletion();

    // --- MAPS / ADDRESS ---
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [searchAddress, setSearchAddress] = useState('');
    const [addressError, setAddressError] = useState<string | null>(null);
    const [modalCity, setModalCity] = useState('');
    const [modalState, setModalState] = useState('');
    const [modalZipCode, setModalZipCode] = useState('');
    const [modalStreetAddress, setModalStreetAddress] = useState('');

    const mainAddressInputRef = useRef<HTMLInputElement | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useAddressAutocomplete(mainAddressInputRef, showAddressModal, (result) => {
        setUserHomeLocation({ latitude: result.latitude, longitude: result.longitude });
        setSearchAddress(result.formattedAddress);
        setModalStreetAddress(result.streetAddress);
        setModalCity(result.city);
        setModalState(result.state);
        setModalZipCode(result.zipCode);
    });

    const loadUserProfile = async () => {
        const data = await fetchUserProfile();
        if (!data) return;

        if (data.homeLocation?.coordinates) {
            setUserHomeLocation({
                longitude: data.homeLocation.coordinates[0] || null,
                latitude: data.homeLocation.coordinates[1] || null
            });
        } else {
            setUserHomeLocation({
                latitude: data.homeLocation?.latitude || null,
                longitude: data.homeLocation?.longitude || null
            });
        }

        const lat = data.homeLocation?.coordinates ? data.homeLocation.coordinates[1] : data.homeLocation?.latitude;
        const lng = data.homeLocation?.coordinates ? data.homeLocation.coordinates[0] : data.homeLocation?.longitude;
        if (!lat || !lng) setShowAddressModal(true);
    };

    const fetchTrees = async () => {
        try {
            const response = await fetchWithAuth('/api/plants');
            if (response.ok) {
                const data = await response.json();
                setTrees(data);
            }
        } catch (err: any) {
            console.error(err.message || 'Network connection to backend server failed.');
        }
    };

    const fetchUserOrders = async () => {
        setOrdersLoading(true);
        try {
            const res = await fetchWithAuth('/api/orders');
            if (res.ok) {
                const data = await res.json();
                setUserOrders(data);
            }
        } catch (err) {
            console.error("Error retrieving account order parameters:", err);
        } finally {
            setOrdersLoading(false);
        }
    };

    useEffect(() => {
        loadUserProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchTrees();
    }, [userHomeLocation]);

    const handleAddressSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAddressError(null);
        const streetTarget = modalStreetAddress || searchAddress;

        if (!streetTarget || !modalCity || !modalState || !modalZipCode || !userHomeLocation.latitude || !userHomeLocation.longitude) {
            setAddressError('Please select a valid structural address selection from the dropdown menu.');
            return;
        }

        setActionLoading(true);
        try {
            const response = await fetchWithAuth('/api/users/profile', {
                method: 'PUT',
                body: JSON.stringify({
                    address: streetTarget,
                    city: modalCity,
                    state: modalState,
                    zipCode: modalZipCode,
                    homeLocation: { latitude: userHomeLocation.latitude, longitude: userHomeLocation.longitude }
                })
            });

            if (response.ok) {
                setShowAddressModal(false);
                loadUserProfile();
                alert("Home context coordinates locked completely!");
            } else {
                throw new Error('Failed to save profile parameters.');
            }
        } catch (error: any) {
            setAddressError(error.message || 'Could not save address configuration.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const missingFields: string[] = [];
        if (!treeName) missingFields.push('name');
        if (!treePrice) missingFields.push('price');
        if (!editingId && !treeImageFile) missingFields.push('image');

        if (missingFields.length > 0) {
            setCustomAlert({ show: true, title: 'Error', message: `Please fill in all fields: ${missingFields.join(', ')}` });
            return;
        }

        if (!userHomeLocation.latitude || !userHomeLocation.longitude) {
            setLocationAlert('required');
            return;
        }

        setActionLoading(true);

        const formData = new FormData();
        formData.append('name', treeName);
        formData.append('price', treePrice);
        formData.append('latitude', String(userHomeLocation.latitude));
        formData.append('longitude', String(userHomeLocation.longitude));
        formData.append('category', 'fruits');
        formData.append('stock', '1');
        formData.append('description', 'Fresh local garden listing.');
        if (treeImageFile) formData.append('image', treeImageFile);

        try {
            const endpoint = editingId ? `/api/plants/${editingId}` : '/api/plants';
            const method = editingId ? 'PUT' : 'POST';

            const response = await fetchWithAuth(endpoint, {
                method: method,
                body: formData,
            });

            if (response.ok) {
                setTreeName('');
                setTreePrice('');
                setTreeImageFile(null);
                setTreeImagePreview('');
                const wasEditing = !!editingId;
                setEditingId(null);
                fetchTrees();

                setCustomAlert({
                    show: true,
                    title: 'Success',
                    message: wasEditing ? 'Tree updated successfully!' : 'Tree added successfully!'
                });
            } else {
                const errData = await response.json();
                setCustomAlert({ show: true, title: 'Error', message: errData.message || 'Failed to save tree configuration logs.' });
            }
        } catch (err: any) {
            setCustomAlert({ show: true, title: 'Error', message: err.message || 'Network communication architecture breakdown.' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleFormCancel = () => {
        setTreeName('');
        setTreePrice('');
        setTreeImageFile(null);
        setTreeImagePreview('');
        setEditingId(null);
    };

    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setTreeImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setTreeImagePreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const startEditMode = (tree: TreeItem) => {
        setEditingId(tree._id);
        setTreeName(tree.name);
        setTreePrice(tree.price.toString());
        setTreeImagePreview(tree.imageUrl);
        document.getElementById('add-tree-form-block')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleDeleteTree = async (id: string) => {
        try {
            const response = await fetchWithAuth(`/api/plants/${id}`, { method: 'DELETE' });
            if (response.ok) {
                fetchTrees();
            } else {
                const errData = await response.json();
                alert(errData.error || "Unable to erase record.");
            }
        } catch (err: any) {
            alert(`Network dispatch error: ${err.message}`);
        }
    };

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await updateProfile({
                name: profileName,
                address: profileAddress,
                city: profileCity,
                state: profileState,
                zipCode: profileZipCode,
                homeLocation: {
                    latitude: userHomeLocation.latitude || 38.5779,
                    longitude: userHomeLocation.longitude || -121.4931
                }
            });
        } catch (err: any) {
            alert(err.message);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col justify-between">
            <Navbar
                session={session} totalItemsCount={totalItemsCount} flashBasket={flashBasket}
                isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen}
                onOpenTrackOrders={() => { setIsTrackOrdersOpen(true); fetchUserOrders(); }}
                onOpenBasket={() => setIsBasketOpen(true)}
                onOpenProfile={() => setIsProfileOpen(true)}
            />

            <div className="relative pt-28 pb-16 w-full bg-gradient-to-b from-white to-green-50/60 overflow-hidden border-b border-gray-100/80">
                <div className="absolute inset-0 opacity-25 z-0 pointer-events-none">
                    <img src="/garden-hero.jpg" alt="Garden Backdrop" className="w-full h-full object-cover" />
                </div>

                <div className="relative z-10 container mx-auto max-w-xl px-4 py-6 rounded-3xl bg-green-500/[0.6] border border-green-500/10 shadow-inner space-y-8">
                    <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-6 shadow-xl border border-white/50">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>Your Trees 🌳</h2>
                        {trees.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 font-medium bg-white/50 rounded-2xl border border-dashed border-gray-300">No trees added yet. Complete the form below!</div>
                        ) : (
                            <div className="space-y-3">
                                {trees.map((tree) => (
                                    <div key={tree._id} className="flex items-center justify-between bg-white p-3 rounded-2xl shadow-sm border border-gray-100 transition-all hover:scale-[1.01]">
                                        <div className="flex items-center gap-4">
                                            <img src={tree.imageUrl.startsWith('http') ? tree.imageUrl : (process.env.REACT_APP_API_URL || 'http://localhost:3000') + tree.imageUrl} alt={tree.name} className="w-16 h-16 object-cover rounded-xl" />
                                            <div>
                                                <h3 className="font-bold text-gray-900 text-lg">{tree.name}</h3>
                                                <p className="text-green-600 font-semibold">${tree.price}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 pr-2">
                                            <button onClick={() => startEditMode(tree)} className="p-2 rounded-full hover:bg-green-50 text-green-600 transition-colors"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-2.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
                                            <button onClick={() => handleDeleteTree(tree._id)} className="p-2 rounded-full hover:bg-red-50 text-red-500 transition-colors"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div id="add-tree-form-block" className="bg-white/80 backdrop-blur-xl rounded-3xl p-6 shadow-xl border border-white/50 scroll-mt-24">
                        <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>{editingId ? 'Modify Tree Details ✏️' : 'Add New Tree 👩‍🌾'}</h2>

                        <form onSubmit={handleFormSubmit} className="space-y-5 text-left">
                            <div>
                                <label className="block text-gray-700 font-semibold text-sm mb-1.5">Tree Name</label>
                                <input type="text" value={treeName} onChange={(e) => setTreeName(e.target.value)} placeholder="Mango, Apple, Orange..." className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-black font-medium bg-white" />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold text-sm mb-1.5">Price / per piece</label>
                                <input type="number" step="0.01" value={treePrice} onChange={(e) => setTreePrice(e.target.value)} placeholder="$0.00" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-black font-medium bg-white" />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-semibold text-sm mb-1.5">Tree Image</label>
                                <label className="w-full flex flex-col items-center justify-center px-4 py-4 border border-gray-300 border-dashed rounded-xl cursor-pointer bg-white text-gray-500 hover:bg-gray-50/50 transition-colors">
                                    <div className="flex items-center gap-2 font-medium text-sm">
                                        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                        <span>{treeImagePreview ? "Change Image File" : "Add Tree Image File"}</span>
                                    </div>
                                    <input type="file" accept="image/*" onChange={handleImageFileChange} className="hidden" />
                                </label>
                                {treeImagePreview && (
                                    <img src={treeImagePreview.startsWith('data:') || treeImagePreview.startsWith('http') ? treeImagePreview : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${treeImagePreview}`} alt="Preview" className="w-24 h-24 object-cover mt-3 rounded-xl border" />
                                )}
                            </div>

                            <div className="flex gap-3">
                                {editingId && (
                                    <button type="button" onClick={handleFormCancel} className="w-1/3 bg-gray-100 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-200 transition-colors text-base">Cancel</button>
                                )}
                                <button type="submit" disabled={actionLoading} className={`${editingId ? 'w-2/3' : 'w-full'} bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 shadow-md text-base transition-colors`}>{actionLoading ? 'Saving...' : editingId ? 'Update Tree' : 'Add Tree'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <AddressModal
                show={showAddressModal} onClose={() => setShowAddressModal(false)} onSubmit={handleAddressSubmit}
                addressError={addressError} searchAddress={searchAddress} setSearchAddress={setSearchAddress}
                modalCity={modalCity} setModalCity={setModalCity} modalState={modalState} setModalState={setModalState}
                modalZipCode={modalZipCode} setModalZipCode={setModalZipCode} actionLoading={actionLoading}
                inputRef={mainAddressInputRef}
            />

            <JoinUpdates profileName={profileName} session={session} />
            <Footer />

            <BasketSidebar
                isOpen={isBasketOpen} onClose={() => setIsBasketOpen(false)}
                basket={basket} updateQuantity={updateQuantity} removeFromBasket={removeFromBasket}
                handleCheckout={handleCheckout} checkoutLoading={checkoutLoading}
                clientSecret={clientSecret} setClientSecret={setClientSecret}
                handlePaymentSuccess={handlePaymentSuccess} grandTotalPrice={grandTotalPrice}
            />

            <ProfileSidebar
                isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)}
                session={session} profileName={profileName} setProfileName={setProfileName}
                profileAddress={profileAddress} profileCity={profileCity}
                profileState={profileState} profileZipCode={profileZipCode}
                isEditingProfile={isEditingProfile} setIsEditingProfile={setIsEditingProfile}
                handleUpdateProfile={handleUpdateProfile} saveLoading={saveLoading}
                handleLogout={handleLogout} handleDeleteAccountClick={() => setShowDeleteConfirm(true)}
                userTreeCount={trees.length}
            />

            <TrackOrdersSidebar
                isOpen={isTrackOrdersOpen} onClose={() => setIsTrackOrdersOpen(false)}
                ordersLoading={ordersLoading} userOrders={userOrders}
            />

            {/* Success / Error Modals */}
            {customAlert && customAlert.show && (
                <div className="fixed inset-0 bg-black/40 z-[300] backdrop-blur-xs flex items-center justify-center p-4 text-left">
                    <div className="bg-[#2d312e] text-white rounded-2xl p-6 max-w-xs w-full shadow-2xl border border-neutral-700 animate-in zoom-in-95 duration-150">
                        <h3 className="text-lg font-bold tracking-tight text-white mb-1.5" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>{customAlert.title}</h3>
                        <p className="text-gray-300 text-sm leading-normal mb-5 font-medium">{customAlert.message}</p>
                        <div className="flex justify-end">
                            <button type="button" onClick={() => setCustomAlert(null)} className="text-green-400 font-extrabold hover:text-green-300 transition-colors text-sm tracking-wider uppercase px-2 py-1">OK</button>
                        </div>
                    </div>
                </div>
            )}

            <DeleteAccountConfirmModal
                show={showDeleteConfirm} onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={executeAccountDeletion} loading={deleteLoading}
            />

            <DeleteAccountSuccessModal show={showDeleteSuccess} onAcknowledge={handleLogout} />

            <PaymentSuccessModal show={showPaymentSuccess} onClose={() => setShowPaymentSuccess(false)} />

            {/* Custom Location Required Prompt Modal */}
            {locationAlert === 'required' && (
                <div className="fixed inset-0 bg-black/50 z-[350] backdrop-blur-xs flex items-center justify-center p-4 text-left">
                    <div className="bg-[#333333] text-white rounded-2xl p-6 max-w-xs w-full shadow-2xl border border-neutral-700/40 animate-in zoom-in-95 duration-150">
                        <h3 className="text-xl font-bold tracking-tight text-white mb-2.5" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                            Location Required
                        </h3>
                        <p className="text-gray-200 text-sm leading-relaxed mb-6 font-medium" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                            No home location set. Please enter your home location to better serve you
                        </p>
                        <div className="flex justify-end gap-5">
                            <button
                                type="button"
                                onClick={() => setLocationAlert('error')}
                                className="text-teal-400 font-extrabold hover:text-teal-300 transition-colors text-xs tracking-wider uppercase"
                                style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => { setLocationAlert(null); setShowAddressModal(true); }}
                                className="text-teal-400 font-extrabold hover:text-teal-300 transition-colors text-xs tracking-wider uppercase"
                                style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}
                            >
                                Enter Home Location
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Location Error Fallback Modal */}
            {locationAlert === 'error' && (
                <div className="fixed inset-0 bg-black/50 z-[350] backdrop-blur-xs flex items-center justify-center p-4 text-left">
                    <div className="bg-[#333333] text-white rounded-2xl p-6 max-w-xs w-full shadow-2xl border border-neutral-700/40 animate-in zoom-in-95 duration-150">
                        <h3 className="text-xl font-bold tracking-tight text-white mb-2.5" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                            Location Error
                        </h3>
                        <p className="text-gray-200 text-sm leading-relaxed mb-6 font-medium" style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}>
                            Could not get valid location. Please check your location settings and try again.
                        </p>
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => setLocationAlert(null)}
                                className="text-teal-400 font-extrabold hover:text-teal-300 transition-colors text-xs tracking-wider uppercase px-2 py-1"
                                style={{ fontFamily: 'Comic Sans MS, Comic Sans, cursive' }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddTreePage;
